import { ColumnDefinition, EntityInterface } from "./Table";

/**
 * CustomerInterface is an interface that defines the structure of a customer object.
 * This is used to safely cast data from the backend as well as sanitize data in the frontend before
 * sending it to the backend
 */
export interface CustomerInterface {
    borrower_id: string;
    commitment_id: number;
    account_id: number;
    borrower_name: string;
    funded_on: string;
    amount: number;
    remaining_limit: number;
    status: string; // TODO: can make status have a stricter type
}

/**
 * Customer is an abstracted interface that contains useful helper functions when dealing
 * with customers
 */
export class Customer implements EntityInterface {
    /** Attribute fields */
    private _id: string;
    private _accountId: number;
    private _commitmentId: number;
    private _borrowerName: string;
    private _fundedOn: string;
    private _accountLimit: number;
    private _availableBalance: number;
    private _status: string; // TODO: can make status have a stricter type

    /**
     * Constructor for the Customer object
     */
    constructor(intf: CustomerInterface) {
        this._id = intf.borrower_id || '0';
        this._accountId = intf.account_id || 0;
        this._commitmentId = intf.commitment_id || 0;
        this._borrowerName = intf.borrower_name?.trim() || "";
        this._fundedOn = intf.funded_on?.trim() || "";
        this._accountLimit = intf.amount || 0;
        this._availableBalance = intf.remaining_limit || 0;
        this._status = intf.status?.toLowerCase().trim() || 'unknown';
    }

    /**
     * Determine if two customers are equal
     * 
     * @param customer the customer to compare against
     * @returns true if the customers are equal, false otherwise
     */
    public equals(customer: Customer): boolean {
        return this.accountId === customer.accountId &&
            this.id === customer.id &&
            this.commitmentId === customer.commitmentId &&
            this.borrowerName === customer.borrowerName &&
            this.fundedOn === customer.fundedOn &&
            this.accountLimit === customer.accountLimit &&
            this.availableBalance === customer.availableBalance &&
            this.status === customer.status;
    }

    /**
     * Return a copy of this customer
     * 
     * @return a copy of this customer
     */
    public copyOf(): Customer {
        return new Customer({
            account_id: this.accountId,
            borrower_id: this.id,
            commitment_id: this.commitmentId,
            borrower_name: this.borrowerName,
            funded_on: this.fundedOn,
            amount: this.accountLimit,
            remaining_limit: this.availableBalance,
            status: this.status
        });
    }

    /**
     * Return the customer as a string representation, used for filtering/searching
     * 
     * We use a unicode full block as a delimeter, so the only string matches that
     * return are from our fields (unless the user is typing the unicode full block
     * character like a maniac)
     */
    public toString(defs: ColumnDefinition<Customer>[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /** Getters */
    public get accountId(): number {
        return this._accountId;
    }

    public get id(): string {
        return this._id;
    }

    public get commitmentId(): number {
        return this._commitmentId;
    }

    public get borrowerName(): string {
        return this._borrowerName;
    }

    public get fundedOn(): string {
        return this._fundedOn;
    }

    public get accountLimit(): number {
        return this._accountLimit;
    }

    public get availableBalance(): number {
        return this._availableBalance;
    }

    public get status(): string {
        return this._status;
    }

    /** Setters */
    public set accountId(accountId: number) {
        this._accountId = accountId;
    }

    public set id(id: string) {
        this._id = id;
    }

    public set commitmentId(commitmentId: number) {
        this._commitmentId = commitmentId;
    }

    public set borrowerName(borrowerName: string) {
        this._borrowerName = borrowerName;
    }

    public set fundedOn(fundedOn: string) {
        this._fundedOn = fundedOn;
    }

    public set accountLimit(accountLimit: number) {
        this._accountLimit = accountLimit;
    }

    public set availableBalance(availableBalance: number) {
        this._availableBalance = availableBalance;
    }

    public set status(status: string) {
        this._status = status;
    }
}

/**
 * CustomerMapper is a service that maps between the Customer model and the CustomerInterface
 * interface that is used to interact with the backend API.
 */
export class CustomerMapper {

  /**
   * toDto takes a Customer and returns a CustomerInterface
   * 
   * @param model the Customer to convert
   * @returns a CustomerInterface
   */
  static toDto(model: Customer): CustomerInterface {
    return {
      borrower_id: model.id,
      commitment_id: model.commitmentId,
      account_id: model.accountId,
      borrower_name: model.borrowerName,
      funded_on: model.fundedOn,
      amount: model.accountLimit,
      remaining_limit: model.availableBalance,
      status: model.status
    };
  }

  /**
   * toModel takes a CustomerInterface and returns a Customer model
   * 
   * @param dto the CustomerInterface to convert
   * @returns a Customer model
   */
  static toModel(dto: CustomerInterface): Customer {
    return new Customer(dto);
  }
}